import { motion } from "framer-motion";
import { GraduationCap, Mic2, Guitar, Globe2, Disc3 } from "lucide-react";
import { ARTISTS } from "@/lib/data";
import { STRINGS } from "@/lib/i18n";
import { useLanguage } from "@/context/LanguageContext";

const MILESTONE_ICONS = [Guitar, GraduationCap, Mic2, Globe2];

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.8, ease: [0.22, 1, 0.36, 1] } }),
};

export default function About() {
  const { lang } = useLanguage();
  const t = STRINGS[lang].about;

  return (
    <section id="professor" className="border-y border-white/5 bg-[#070B14] py-28 lg:py-36" data-testid="about-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-14 lg:grid-cols-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="lg:col-span-4"
          >
            <div className="lg:sticky lg:top-28">
              <div className="relative overflow-hidden rounded-2xl border border-blue-500/20 shadow-[0_0_45px_rgba(59,130,246,0.12)]">
                <img
                  src="/about-quem.jpg"
                  alt={t.portraitCaption}
                  className="w-full object-cover"
                  data-testid="about-photo-quem"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#05070D]/70 via-transparent to-transparent" aria-hidden="true" />
                <p className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.22em] text-blue-200/80">
                  {t.portraitCaption}
                </p>
              </div>
              <p className="mt-6 border-l-2 border-blue-500/50 pl-5 font-serif text-xl italic text-blue-100/90" data-testid="about-quote">
                "{t.quote}"
              </p>
            </div>
          </motion.div>

          <div className="lg:col-span-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={fadeUp}>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-blue-400/80">{t.overline}</p>
              <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
                {t.title[0]}
                <em className="italic text-blue-400">{t.title[1]}</em>
                {t.title[2]}
              </h2>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300/90">{t.lead}</p>
            </motion.div>

            <div className="mt-12 space-y-2">
              {t.milestones.map((m, i) => {
                const Icon = MILESTONE_ICONS[i];
                return (
                  <motion.div
                    key={m.title}
                    custom={i}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-60px" }}
                    variants={fadeUp}
                    className="flex gap-5 border-b border-white/5 py-6"
                    data-testid={`about-milestone-${i}`}
                  >
                    <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-blue-500/25 bg-blue-500/5">
                      <Icon className="h-4.5 w-4.5 text-blue-400" />
                    </span>
                    <div>
                      <h3 className="font-serif text-xl text-blue-50">{m.title}</h3>
                      <p className="mt-1.5 max-w-xl text-sm leading-relaxed text-slate-400">{m.text}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp}
              className="mt-10"
              data-testid="about-artists"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{t.artistsLabel}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {ARTISTS.map((artist) => (
                  <span
                    key={artist}
                    className="rounded-full border border-blue-500/20 bg-blue-500/5 px-4 py-1.5 text-xs text-blue-200/90"
                    data-testid={`about-artist-${artist.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {artist}
                  </span>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp}
              className="mt-10"
              data-testid="about-igt-photos"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{t.igtLabel}</p>
              <div className="mt-4 grid grid-cols-3 gap-3">
                {["/about-igt-1.jpg", "/about-igt-2.jpg", "/about-igt-3.jpg"].map((src, i) => (
                  <div key={src} className="group overflow-hidden rounded-xl border border-white/10" data-testid={`about-igt-photo-${i + 1}`}>
                    <img
                      src={src}
                      alt={`${t.igtAlt} — ${i + 1}`}
                      loading="lazy"
                      className="h-44 w-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105 sm:h-64"
                    />
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          variants={fadeUp}
          className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
          data-testid="about-gallery"
        >
          {t.gallery.map(([src, caption], i) => (
            <figure key={src} className="group overflow-hidden rounded-xl border border-white/10" data-testid={`about-gallery-${i}`}>
              <div className="h-44 overflow-hidden">
                <img
                  src={src}
                  alt={caption}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
              </div>
              <figcaption className="flex items-center gap-2 bg-[#0B101C] px-4 py-3">
                <Disc3 className="h-3.5 w-3.5 shrink-0 text-blue-400/70" />
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">{caption}</span>
              </figcaption>
            </figure>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
